import {useCallback, useEffect, useState} from 'react';
import {useDelayedFlag} from '@chekinapp/ui';
import {STATUSES} from '@chekin/core';
import {riseHomeBlocks} from 'components/EntranceScreen/entranceExit';
import {useChekinSDK} from 'context/ChekinSDK';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {useComputedOnboardingDetails} from 'hooks/useComputedOnboardingDetails';
import {useGetReservationType} from 'hooks/useGetReservationType';
import {useInstantCheckInOffer} from 'pages/InstantCheckIn/useInstantCheckInOffer';
import {useFirstScreenStore} from 'utils/firstScreen';
import {isDesktopViewport} from 'utils/isDesktopViewport';
import {
  type BookingFoundHandoff,
  useBookingFoundHandoffStore,
} from '../useBookingFoundHandoffStore';
import {REDIRECT_DELAY_MS} from '../useBookingFoundRedirect';
import {BookingFoundScreen} from './BookingFoundScreen';
import {FoundGuestCount} from './FoundGuestCount';

export const BOOKING_FOUND_TIMING = {
  minHold: REDIRECT_DELAY_MS,
  countHold: 2750,
  countHoldDesktop: 220,
  maxHold: 15000,
  grow: 830,
  leave: 420,
};

const HOME_RISE_DELAY_MS = 80;

type Stage = 'welcome' | 'count' | 'growing';

const getCountHold = () =>
  isDesktopViewport()
    ? BOOKING_FOUND_TIMING.countHoldDesktop
    : BOOKING_FOUND_TIMING.countHold;

function HandoffScreen({
  handoff,
  onDone,
}: {
  handoff: BookingFoundHandoff;
  onDone: () => void;
}) {
  const {isSuccessSDK, isAuthInitializationError} = useChekinSDK();
  const {data: reservation, housing, isLoading: isReservationLoading} = useReservation();
  const {summaryStatus} = useSummary();
  const offer = useInstantCheckInOffer();
  const {shouldAskNumberOfGuests, isLoadingDetails} = useComputedOnboardingDetails();
  const {hasReservationGroup, isLoaded: isReservationTypeLoaded} =
    useGetReservationType();
  const screenReadyAt = useFirstScreenStore(state => state.readyAt);
  const isHeldLongEnough = useDelayedFlag(true, BOOKING_FOUND_TIMING.minHold);
  const [countHold] = useState(getCountHold);
  const isHeldForCount = useDelayedFlag(true, countHold);
  const isTimedOut = useDelayedFlag(true, BOOKING_FOUND_TIMING.maxHold);
  const [stage, setStage] = useState<Stage>('welcome');
  const [totalGuests, setTotalGuests] = useState<number>();
  const [isLeaving, setIsLeaving] = useState(false);

  const isSessionReady =
    isSuccessSDK &&
    reservation?.id === handoff.reservation.id &&
    !isReservationLoading &&
    Boolean(housing?.id) &&
    summaryStatus === STATUSES.success &&
    offer.isDecided &&
    !isLoadingDetails &&
    isReservationTypeLoaded;
  const isScreenReady = screenReadyAt > handoff.startedAt;
  const isFailed = isAuthInitializationError || summaryStatus === STATUSES.error;
  const isWelcome = stage === 'welcome';
  const shouldConfirmGuests = shouldAskNumberOfGuests && !hasReservationGroup;
  const shouldAskCount =
    isWelcome && !isFailed && isSessionReady && isHeldForCount && shouldConfirmGuests;
  const shouldLeave =
    isWelcome &&
    !shouldAskCount &&
    (isFailed ||
      isTimedOut ||
      (isHeldLongEnough && isSessionReady && isScreenReady && !shouldConfirmGuests));

  useEffect(
    function raiseTheGuestCount() {
      if (shouldAskCount) {
        setStage('count');
      }
    },
    [shouldAskCount],
  );

  useEffect(
    function leaveOnceTheBookingIsReady() {
      if (!shouldLeave) {
        return;
      }

      setIsLeaving(true);
      const timeout = setTimeout(onDone, BOOKING_FOUND_TIMING.leave);
      return () => clearTimeout(timeout);
    },
    [shouldLeave, onDone],
  );

  useEffect(
    function fadeIntoTheHomeOnceConfirmed() {
      if (stage !== 'growing') {
        return;
      }

      const fade = setTimeout(() => {
        setIsLeaving(true);
        riseHomeBlocks(HOME_RISE_DELAY_MS);
      }, BOOKING_FOUND_TIMING.grow);
      const close = setTimeout(
        onDone,
        BOOKING_FOUND_TIMING.grow + BOOKING_FOUND_TIMING.leave,
      );
      return () => {
        clearTimeout(fade);
        clearTimeout(close);
      };
    },
    [stage, onDone],
  );

  const handleConfirmed = useCallback(() => setStage('growing'), []);

  return (
    <BookingFoundScreen
      reservation={handoff.reservation}
      guestName={handoff.guestName}
      propertyName={handoff.propertyName}
      isNew={handoff.isNew}
      isLeaving={isLeaving}
      isGrowing={stage === 'growing'}
      isStraightToHome={isSessionReady && !shouldConfirmGuests}
      guests={totalGuests}
    >
      {!isWelcome && (
        <FoundGuestCount onDone={handleConfirmed} onTotalChange={setTotalGuests} />
      )}
    </BookingFoundScreen>
  );
}

function BookingFoundOverlay() {
  const handoff = useBookingFoundHandoffStore(state => state.handoff);
  const close = useBookingFoundHandoffStore(state => state.close);

  if (!handoff) {
    return null;
  }

  return <HandoffScreen key={handoff.startedAt} handoff={handoff} onDone={close} />;
}

export {BookingFoundOverlay};
